// api/notesApi.js - Refactored for workspace-scoped notes (formerly stickiesApi.js)

const API_BASE_URL = '/api';

const handleApiError = async (response, customMessage) => {
  if (!response.ok) {
    let errorMessage = customMessage;
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorMessage;
    } catch (e) {
      errorMessage = `${customMessage}: ${response.statusText}`;
    }
    throw new Error(errorMessage);
  }
};

// ============================================================================
// WORKSPACE NOTES API (formerly stickies)
// ============================================================================

/**
 * Fetch all notes for a workspace
 * @param {string} workspacePublicId - The public ID of the workspace
 * @returns {Promise<Note[]>} Array of notes
 */
export const fetchNotes = async (workspacePublicId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/workspaces/${workspacePublicId}/notes`, {
      credentials: 'include'
    });
    
    await handleApiError(response, 'Failed to fetch notes');
    return await response.json();
  } catch (error) {
    console.error('Error fetching notes:', error);
    throw error;
  }
};

/**
 * Create a new note in a workspace
 * @param {string} workspacePublicId - The public ID of the workspace
 * @param {Object} noteData - The note data
 * @returns {Promise<Note>} The created note
 */
export const createNote = async (workspacePublicId, noteData) => {
  try {
    const response = await fetch(`${API_BASE_URL}/workspaces/${workspacePublicId}/notes`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(noteData)
    });

    await handleApiError(response, 'Failed to create note');
    return await response.json();
  } catch (error) {
    console.error('Error creating note:', error);
    throw error;
  }
};

/**
 * Update an existing note
 * @param {string} workspacePublicId - The public ID of the workspace
 * @param {number} noteId - The ID of the note to update
 * @param {Object} noteData - The note data to update
 * @returns {Promise<Note>} The updated note
 */
export const updateNote = async (workspacePublicId, noteId, noteData) => {
  try {
    const response = await fetch(`${API_BASE_URL}/workspaces/${workspacePublicId}/notes/${noteId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(noteData)
    });

    await handleApiError(response, 'Failed to update note');
    return await response.json();
  } catch (error) {
    console.error('Error updating note:', error);
    throw error;
  }
};

/**
 * Delete a note
 * @param {string} workspacePublicId - The public ID of the workspace
 * @param {number} noteId - The ID of the note to delete
 * @returns {Promise<boolean>} True if deletion was successful
 */
export const deleteNote = async (workspacePublicId, noteId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/workspaces/${workspacePublicId}/notes/${noteId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include'
    });

    await handleApiError(response, 'Failed to delete note');
    return true;
  } catch (error) {
    console.error('Error deleting note:', error);
    throw error;
  }
};

/**
 * Upload an image and create a new image note
 * @param {string} workspacePublicId - The public ID of the workspace
 * @param {File} imageFile - The image file to upload
 * @param {number} x - X position
 * @param {number} y - Y position
 * @returns {Promise<Object>} The created image note
 */
export const uploadImageNote = async (workspacePublicId, imageFile, x = 250, y = 200) => {
  try {
    const formData = new FormData();
    formData.append('file', imageFile);
    formData.append('x', x);
    formData.append('y', y);

    const response = await fetch(
      `${API_BASE_URL}/workspaces/${workspacePublicId}/notes/upload`,
      {
        method: 'POST',
        credentials: 'include',
        body: formData
      }
    );

    await handleApiError(response, 'Failed to upload image note');
    return await response.json();
  } catch (error) {
    console.error('Error uploading image note:', error);
    throw error;
  }
};

/**
 * Upload an image for a note
 * @param {string} workspacePublicId - The public ID of the workspace
 * @param {number} noteId - The ID of the note
 * @param {File} imageFile - The image file to upload
 * @returns {Promise<Object>} The uploaded image data with image_url
 */
export const uploadNoteImage = async (workspacePublicId, noteId, imageFile) => {
  try {
    const formData = new FormData();
    formData.append('image', imageFile);

    const response = await fetch(
      `${API_BASE_URL}/workspaces/${workspacePublicId}/notes/${noteId}/upload-image`,
      {
        method: 'POST',
        credentials: 'include',
        body: formData
      }
    );

    await handleApiError(response, 'Failed to upload note image');
    return await response.json();
  } catch (error) {
    console.error('Error uploading note image:', error);
    throw error;
  }
};

/**
 * Update note layouts in bulk
 * @param {string} workspacePublicId - The public ID of the workspace
 * @param {Array} layoutUpdates - Array of layout update objects
 * @returns {Promise<boolean>} True if update was successful
 */
export const updateNoteLayouts = async (workspacePublicId, layoutUpdates) => {
  try {
    const response = await fetch(`${API_BASE_URL}/workspaces/${workspacePublicId}/notes/batch`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ notes: layoutUpdates })
    });

    // ADD THIS BLOCK:
    if (!response.ok) {
      const errorDetail = await response.json();
      console.error('❌ Backend validation error:', errorDetail);
    }

    await handleApiError(response, 'Failed to update note layouts');
    return true;
  } catch (error) {
    console.error('Error updating note layouts:', error);
    throw error;
  }
};

/**
 * Fetch tag domains from system config
 * Global configuration endpoint (not workspace-specific)
 * @returns {Promise<Object>} Tag domains object
 */
export async function fetchTagDomains() {
  try {
    const response = await fetch('/api/config/tag-domains', {
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include'
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch tag domains');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching tag domains:', error);
    throw error;
  }
}

/**
 * Create multiple notes in batch
 * @param {string} workspacePublicId - The public ID of the workspace
 * @param {Array} notesData - Array of note data objects
 * @returns {Promise<Object>} Response with created notes
 */
export const createNotesBatch = async (workspacePublicId, notesData) => {
  try {
    const response = await fetch(`${API_BASE_URL}/workspaces/${workspacePublicId}/notes/batch`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ notes: notesData })
    });

    await handleApiError(response, 'Failed to create notes batch');
    const result = await response.json();
    return result.notes;  // Backend returns { message, notes }
  } catch (error) {
    console.error('Error creating notes batch:', error);
    throw error;
  }
};